import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setUndoRedoCommand } from '../../../redux/operationSlice';

/**
 * useRedo Hook
 *
 * Redux'taki undoRedoCommand state'ini dinler.
 * 'REDO' komutu geldiğinde, global undoRedoManager üzerinden
 * redo işlemini gerçekleştirir.
 *
 * Async REDO desteği:
 * - restoreCallback kullanan komutlar (Socket, Machine, Lighting, MainPanel, Panel) için
 *   chain advancement, async işlem tamamlandıktan sonra yapılır
 * - Sync komutlar için eski davranış korunur
 *
 * @param {THREE.Scene} scene - Three.js sahne nesnesi
 * @param {object} options - Ek ayarlar
 * @param {React.MutableRefObject} options.undoRedoManagerRef - UndoRedoManager ref'i
 */
export function useRedo(scene, options = {}) {
  const dispatch = useDispatch();
  const undoRedoCommand = useSelector(state => state.operation.undoRedoCommand);

  useEffect(() => {
    if (undoRedoCommand !== 'REDO') return;

    const undoRedoManager = options.undoRedoManagerRef?.current ||
                           (typeof window !== 'undefined' ? window.undoRedoManager : null);

    if (!undoRedoManager) {
      dispatch(setUndoRedoCommand('NONE'));
      return;
    }

    // Position zaten son element'te mi kontrol et
    if (undoRedoManager.position >= undoRedoManager.history.length - 1) {
      dispatch(setUndoRedoCommand('NONE'));
      return;
    }

    // Global redo işlemi
    try {
      // Completion callback ile redo çağır
      undoRedoManager.redo(scene, dispatch, () => {
        // Bu callback async REDO tamamlandığında çağrılır (notifyRedoComplete'den)

        // Zincir ilerletme: Bir sonraki transaction var mı?
        const nextTx = undoRedoManager.history[undoRedoManager.position + 1];
        if (nextTx) {
          // FIX: Önce NONE dispatch et, sonra REDO - böylece useEffect yeniden tetiklenir
          dispatch(setUndoRedoCommand('NONE'));
          setTimeout(() => {
            dispatch(setUndoRedoCommand('REDO'));
          }, 10);
        } else {
          dispatch(setUndoRedoCommand('NONE'));
        }
      });

      // Eğer sync komut ise (pendingRedo = false), hemen chain advancement yap
      if (!undoRedoManager.pendingRedo) {
        // Zincir ilerletme (sync komutlar için)
        setTimeout(() => {
          const nextTx = undoRedoManager.history[undoRedoManager.position + 1];
          if (nextTx) {
            dispatch(setUndoRedoCommand('REDO'));
          } else {
            dispatch(setUndoRedoCommand('NONE'));
          }
        }, 100);
      }
      // Async komut - chain advancement callback'te yapılacak

    } catch (err) {
      dispatch(setUndoRedoCommand('NONE'));
    }

  }, [undoRedoCommand, scene, dispatch, options.undoRedoManagerRef]);
}
